import { useT } from "../../../../i18n/I18nProvider";
import { Card, CardHead, fmtNum } from "../../../ui";
import { leadSourceLabel, mergeLeadSources } from "../../../../lib/leadSource";
import type { DashSource } from "../../../../lib/dashboard";

const RING = [
  "var(--color-primary)",
  "var(--color-primary-2)",
  "var(--color-success)",
  "var(--color-warning)",
  "var(--color-destructive)",
  "var(--color-muted-foreground)",
];

const R = 52;
const CIRC = 2 * Math.PI * R;

/**
 * Where the leads came from, as a ring.
 *
 * Drawn as one circle per slice with a dash offset rather than as arc paths:
 * the segments then share a single geometry, so a slice that rounds to under a
 * degree still renders as a hairline instead of collapsing into nothing.
 *
 * The labels come from `lib/leadSource`, shared with the juhuklient board. This
 * card used to keep its own list of known values under its own `dash.source.*`
 * keys, and the list had drifted: it translated `manual` and `scanner`, which
 * nothing writes, while the values the platform really stores went through
 * untranslated - the biggest slice on the ring read "site-scanner".
 */
export function SourcesCard({ sources }: { sources: DashSource[] }) {
  const t = useT();
  // Folded before anything is measured: the API counts the raw column, and two
  // spellings of one origin must not become two slices with one label.
  const rows = mergeLeadSources(sources);
  const total = rows.reduce((s, r) => s + r.count, 0);

  let offset = 0;
  const slices = rows.map((row, i) => {
    const len = total ? (row.count / total) * CIRC : 0;
    const slice = { ...row, len, offset, color: RING[i % RING.length]! };
    offset += len;
    return slice;
  });

  return (
    <Card>
      <CardHead title={t("dash.sources.title")} sub={t("dash.sources.sub")} />
      <div className="crm-donut-wrap">
        <div className="crm-donut">
          <svg viewBox="0 0 148 148" width="148" height="148" aria-hidden>
            <circle cx="74" cy="74" r={R} fill="none" stroke="var(--crm-sunk)" strokeWidth="18" />
            {slices.map((s) => (
              <circle
                key={s.source}
                cx="74"
                cy="74"
                r={R}
                fill="none"
                stroke={s.color}
                strokeWidth="18"
                strokeDasharray={`${s.len} ${CIRC}`}
                strokeDashoffset={-s.offset}
              />
            ))}
          </svg>
          <div className="crm-donut-mid">
            <div>
              <div className="crm-donut-n">{fmtNum(total)}</div>
              <div className="crm-donut-l">{t("dash.sources.unit")}</div>
            </div>
          </div>
        </div>
        <div className="crm-legend2">
          {slices.map((s) => (
            <span key={s.source} className="crm-lg">
              <i style={{ background: s.color }} />
              <span className="crm-lgn">{leadSourceLabel(s.source, t)}</span>
              <span className="crm-lgv">{fmtNum(s.count)}</span>
              <span className="crm-lgp">{total ? Math.round((s.count / total) * 100) : 0}%</span>
            </span>
          ))}
        </div>
      </div>
    </Card>
  );
}
